<template>
  <div>
    <loadingCube v-if="loading"/>
    <table v-else class="ns-table">
      <thead>
      <tr>
        <th :style="zIndexReverse(3, 0)" class="">
          <div class="capsule"><span>Note type</span></div>
        </th>
        <th v-if="isWindowed" :style="zIndexReverse(3, 1)" class="rotate">
          <div class="capsule"><span>Window</span></div>
        </th>
        <th v-for="m in metricOrder" :key="m" :style="zIndexReverse(3, 2)" class="rotate">
          <div class="capsule"><span>{{ metricLabels[m] || m }}</span></div>
        </th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="row in rows" :key="rowKey(row)">
        <td>
          <NotesQuickLink
              mode="notes-global"
              :query="{...query, match: row.note_type_ident }"
              :params="params"
          >
            {{ noteTypeLabels[row.note_type_ident] || row.note_type_ident }}
          </NotesQuickLink>
        </td>
        <td v-if="isWindowed">{{ windowLabels[row.time_window] || row.time_window }}</td>
        <td v-for="m in metricOrder" :key="m">{{ toInt(row[m]) }}</td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" scoped>
@use "../../scss/shared" as *;
</style>

<script>
import loadingCube from 'loaders/record_loader';
import { zIndexReverse } from "pages/sunset/reports/notestats/components/shared/utils";
import NotesQuickLink from "pages/sunset/reports/notestats/components/shared/notes_link.vue";

export default {
  name: 'sunset_note_stats_global',
  components: {NotesQuickLink, loadingCube },
  props: {
    params: { type: Object, required: true }, // { side,start,end,windowed,mask,match,notMatch,includeOther,apiBaseUrl,... }
    meta:   { type: Object, required: true },
    // NEW: optional, from <sunset-note-stats-query-bar v-slot="{ ..., query }">
    query:  { type: Object, default: () => ({}) },
  },
  data() {
    return {
      loading: true,
      rows: [],
      refreshTimer: null,
      defaultApiBaseUrl: '/api/v1/',
    };
  },
  computed: {
    apiBase() {
      const base = this.params?.apiBaseUrl || this.defaultApiBaseUrl;
      return String(base).replace(/\/+$/, '');
    },

    // unify params + query: query wins if present
    effectiveQuery() {
      const q = { ...(this.params || {}), ...(this.query || {}) };

      // normalize booleans/aliases
      const includeOther = this.normalizeBool(q.includeOther ?? q.include_other ?? true, true);
      const windowed     = this.normalizeBool((q.windowed ?? q.by_window ?? q.bywindow ?? this.params?.windowed), false);

      return {
        side:  String(q.side || this.params?.side || 'combined').toLowerCase(),
        mask:  String(q.mask || this.params?.mask || 'all'),
        start: q.start || this.params?.start || '',
        end:   q.end   || this.params?.end   || '',
        match: q.match ?? this.params?.match ?? '',
        not:   (q.not ?? q.notMatch ?? this.params?.notMatch ?? ''),
        includeOther,
        windowed,
        // pass through any extras (won't hurt; server can ignore)
        user: q.user ?? q.userId ?? q.user_id,
        limit: q.limit,
        perPage: q.perPage,
        page: q.page,
      };
    },

    isWindowed() { return this.effectiveQuery.windowed; },

    metricOrder()  { return Array.isArray(this.meta?.metrics) ? this.meta.metrics : []; },
    metricLabels() { return this.meta?.metrics_labels || {}; },
    windowLabels() { return this.meta?.window_labels || {}; },
    noteTypeLabels(){ return this.meta?.note_types?.labels || {}; },

    requestUrl() {
      const q = this.effectiveQuery;
      const usp = new URLSearchParams();

      usp.set('side', q.side);
      usp.set('mask', q.mask);
      if (q.start) usp.set('start', q.start);
      if (q.end)   usp.set('end', q.end);
      usp.set('includeOther', q.includeOther ? '1' : '0');
      if (q.windowed) usp.set('windowed', '1');
      if (q.match) usp.set('match', q.match);
      if (q.not)   usp.set('not', q.not);

      // harmless pass-throughs
      if (q.user != null)   usp.set('user', String(q.user));
      if (q.limit != null)  usp.set('limit', String(q.limit));
      if (q.perPage != null)usp.set('perPage', String(q.perPage));
      if (q.page != null)   usp.set('page', String(q.page));

      return `${this.apiBase}/sunset/reports/note-stats/totals/global?${usp.toString()}`;
    },
  },
  watch: {
    params: { handler() { this.scheduleFetch(); }, deep: true },
    query:  { handler() { this.scheduleFetch(); }, deep: true }, // NEW
  },
  async mounted() { await this.fetchCoreSummary(); },
  beforeDestroy() { if (this.refreshTimer) clearTimeout(this.refreshTimer); },
  methods: {
    zIndexReverse,
    normalizeBool(value, fallback = false) {
      if (typeof value === 'boolean') return value;
      const s = String(value ?? '').toLowerCase();
      if (['1','true','yes','on'].includes(s)) return true;
      if (['0','false','no','off'].includes(s)) return false;
      return fallback;
    },
    toInt(v) { const n = Number(v); return Number.isFinite(n) ? n : 0; },
    rowKey(row) { return this.isWindowed ? `${row.note_type_ident}__${row.time_window || 'all'}` : row.note_type_ident; },
    scheduleFetch() {
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => { this.fetchCoreSummary(); }, 120);
    },
    async fetchCoreSummary() {
      const { start, end } = this.effectiveQuery;
      if (!start || !end) { this.rows = []; this.loading = false; return; }
      this.loading = true;
      try {
        const res = await axios.get(this.requestUrl);
        const payload = res && res.data ? res.data : { data: [] };
        this.rows = Array.isArray(payload.data) ? payload.data : [];
      } catch { this.rows = []; }
      finally { this.loading = false; }
    },
  },
};
</script>
